<template>
  <v-layout id="summary" column pa-1>
    <v-card v-for="(collection, type) in collections" :key="type" class="mb-4 mx-5 intro-dark" flat>
      <v-card-title class="justify-center font-weight-bold" :id="`${type}-header`">
        <mrx-translation :token="`app.summary.${type}.title`"></mrx-translation>
      </v-card-title>
      <v-card-text class="assessment-light panel-opaque pt-4">
        <div v-for="(panel, index) in collection" :key=index>
          <list class="assessment-light panel-opaque" v-bind:item="panel" :key="panel.id" :ref="`${type}List${index}`">
          </list>
          <v-divider v-if="index < collection.length - 1" :key="`${panel.id}-divider`"></v-divider>
        </div>
      </v-card-text>
    </v-card>
    
    <div
      v-if="!isPreferencesSummary && allowCopyPaste"
      class="d-flex flex-column align-center pt-2 pb-2 mx-5"
      style="width: 90%;"
    >
      <CopyClipboardButton
        token="app.assessment.summary.button.copy.title"
        name="summary"
        cssClass="copy-all-btn"
        :payload="allSummaryPayload"
        mode="button"
        style="width: 100%;"
      />
    </div>
      
    <v-card
      id="user-interface"
      v-if="isPreferencesSummary && allowEditUserInterface"
      class="mb-4 mx-5 intro-dark"
      flat
    >
      <v-card-title class="justify-center font-weight-bold">
        <mrx-translation token="app.summary.user_interface.title"></mrx-translation>
      </v-card-title>
      <v-card-text class="assessment-light panel-opaque pt-4">
      <div v-for="userInterfaceItem in userInterfaceItemsList" :key="userInterfaceItem.id">
        <list
        class="assessment-light panel-opaque"
        :item="userInterfaceItem"
        :key="userInterfaceItem.id"
        style="cursor:pointer;"
        />
      </div>
      </v-card-text>
    </v-card>
  </v-layout>
</template>

<script>
import Bottom from '@/components/layouts/Bottom'
import List from '@/components/layouts/List'
import CopyClipboardButton from '@/components/widgets/CopyClipboardButton'
import groupBy from 'lodash.groupby'
import { mapGetters } from 'vuex'
import ui from '@/mixins/ui'
import { addLocationCollection } from '@/utils/location'
import { SummaryPayloadBuilder } from '@/utils/summaryPayloadUtils'

export default {
  mixins: [ui],
  components: { Bottom, List, CopyClipboardButton },
  name: 'Summary',
  
  data() {
    return {
      collections: null,
      title: null,
      nextLink: null,
      allCopyPayloads: {},
      userInterfaceItemsList: [
        {
          id: 'user-interface',
          name: 'user_interface',
          editRoutePath: 'user_interface',
          responses: []
        }
      ]
    }
  },

  props: {
    maxHeightOffset: {
      type: Number,
      default: 0
    },
    initialMaxHeightOffset: {
      type: Number,
      default: 0
    }
  },

  computed: {
    ...mapGetters({
      track: 'v4/track/current',
      acceptedObservations: 'v4/observation/accepted',
      rejectedObservations: 'v4/observation/rejected',
      locationSummary: 'v4/location/summary',
    }),

    allowCopyPaste() {
      return this.$store.state.settings.engagements_features.allowCopyPaste
    },

    allowEditUserInterface() {
      return this.$store.state.settings.engagements_features.editUserInterface
    },

    isPreferencesSummary() {
      return this.$route.meta.collection === 'preference';
    },

    allSummaryPayload() {
      const rtl = this.$store.state.v4.language?.rtl;
      const builder = new SummaryPayloadBuilder(this, rtl);
      return builder.buildCombinedPayload(this.allCopyPayloads);
    },
  },

  methods: {
    async collection() {
      const collection = await this.fetchCollection();
      this.collections = groupBy(collection, 'collections');
    },

    async fetchCollection() {
      const collectionType = this.$route.meta.collection;
      let collection = null;

      switch (collectionType) {
        case 'panel':
          const panels = this.getPanelCollection();
          const calculated = await this.getCalculatedPanels();
          collection = [...panels, ...calculated];
          this.title = 'app.summary.title';
          break;

        case 'observation':
          collection = this.getObservationCollection();
          this.title = 'app.observations.title';
          this.prompt = 'app.observations.prompt';
          break;

        case 'preference':
          collection = this.getPreferenceCollection();
          this.title = 'app.preferences.title';
          break;
      }

      return this.filterCollection(collection);
    },

    getPanelCollection() {
      const panelHasSelectedResponses = (panel) => 
        panel.responses && panel.responses.some(response => response.selected);

      return this.track.panels.filter(panel => panelHasSelectedResponses(panel));
    },

    async getCalculatedPanels() {
      // currently implemented just for the bmi calculation
      // if this is expanded, it would be worth it to extract out into its own utility
      const calculated = await this.$store.dispatch('v4/panel/responses');
      const bmi = calculated.find(p => p.name === 'body_mass_index');
      if (!bmi) return [];
      // convert the mrx bundle response format back into a panel format
      const asPanel = {
        id: bmi.name, // needed for list item key
        name: bmi.name, // needed for list name
        type: 'value', // ensures the proper response format is shown
        collections: 'patient', // needed for proper grouping in summary
        hideEdit: true, // hides the edit button
        responses: [{
          name: bmi.name, // needed for response item id
          value: bmi.value, // needed for display value
          id: bmi.name, // needed for response iterator key
          selected: true, // ensures response is shown
        }]
      }
      return [asPanel];
    },

    getObservationCollection() {
      return [].concat(this.acceptedObservations, this.rejectedObservations);
    },

    getPreferenceCollection() {
      return addLocationCollection(
        this.$store.state.preferences,
        this.locationSummary,
      );
    },

    filterCollection(collection) {
      let filtered = [...collection];

      if (!this.$store.state.v4.track.comprehensive) {
        filtered = this.markNonEditablePanels(filtered);
      }

      return filtered.filter(panel => {
        if (panel.name === 'summary') return false;
        if (this.$store.getters['v4/mode/get']('testing')) return true;
        if (panel.collections === 'system') return false;
        return true;
      });
    },

    markNonEditablePanels(collection) {
      const streamlinedIndex = collection.findIndex(p => p.name === 'streamlined_choice');
      
      return collection.map((panel, index) => {
        if (index < streamlinedIndex) {
          return { ...panel, hideEdit: true };
        }
        return panel;
      });
    },

    updateAllCopyPayloads() {
      setTimeout(() => {
        if (!this.$refs || Object.keys(this.$refs).length === 0) {
          return;
        }
        const rtl = this.$store.state.v4.language && this.$store.state.v4.language.rtl;
        const builder = new SummaryPayloadBuilder(this, rtl);
        this.allCopyPayloads = builder.buildAllPayloads();
      }, 300);
    }
  },

  mounted() {
    this.$store.subscribeAction((action) => {
      if (action.type === 'v4/language/set') {
        this.updateAllCopyPayloads();
      }
    });

    this.$store.commit('v4/track/set', {
      editResponsesMode: false,
      editingFollowupResponses: false,
    });

    this.collection();

    if (this.$route.meta.collection === 'preference') {
      this.$store.dispatch('v4/reset');
    }
  },

  updated() {
    this.$nextTick(() => {
      if (this.$route.params.offset) {
        window.scroll(0, this.$route.params.offset);
      }
      this.updateAllCopyPayloads();
    });
  },

  watch: {
    '$route.name'() {
      this.collection();
    },
    
    '$state.store.observation.v4.fhir'() {
      this.collection();
    },
    
     '$store.state.locale'() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.updateAllCopyPayloads();
        }, 50);
      });
    }
  }
}
</script>

<style scoped></style>